import {
  defineMessages,
  type IntlShape,
  type MessageDescriptor,
  type PrimitiveType,
} from "react-intl";

import type { FacilityCategoryKey } from "@/features/hotels/utils/facilities/types";

const CATEGORY_MESSAGES = defineMessages<FacilityCategoryKey>({
  "core-essentials": {
    defaultMessage: "Core Essentials",
    description: "Hotel facilities category heading for core essentials",
  },
  accessibility: {
    defaultMessage: "Accessibility",
    description: "Hotel facilities category heading for accessibility",
  },
  safety: {
    defaultMessage: "Safety",
    description: "Hotel facilities category heading for safety",
  },
  leisure: {
    defaultMessage: "Leisure",
    description: "Hotel facilities category heading for leisure",
  },
  dining: {
    defaultMessage: "Dining",
    description: "Hotel facilities category heading for dining",
  },
  service: {
    defaultMessage: "Service",
    description: "Hotel facilities category heading for service",
  },
  sports: {
    defaultMessage: "Sports",
    description: "Hotel facilities category heading for sports",
  },
  "core-support": {
    defaultMessage: "Core Support",
    description: "Hotel facilities category heading for core support",
  },
  wellness: {
    defaultMessage: "Wellness",
    description: "Hotel facilities category heading for wellness",
  },
  family: {
    defaultMessage: "Family",
    description: "Hotel facilities category heading for family",
  },
  business: {
    defaultMessage: "Business",
    description: "Hotel facilities category heading for business",
  },
  entertainment: {
    defaultMessage: "Entertainment",
    description: "Hotel facilities category heading for entertainment",
  },
  secondary: {
    defaultMessage: "Secondary",
    description: "Hotel facilities category heading for secondary",
  },
  policies: {
    defaultMessage: "Policies",
    description: "Hotel facilities category heading for policies",
  },
  admin: {
    defaultMessage: "Admin",
    description: "Hotel facilities category heading for admin",
  },
  extra: {
    defaultMessage: "Extra",
    description: "Hotel facilities category heading for unmatched facilities",
  },
});

const FACILITY_MESSAGES = defineMessages({
  wifi: {
    defaultMessage: "Wifi",
    description: "Hotel facility label for wifi",
  },
  "air-conditioning-public-areas": {
    defaultMessage: "AC",
    description: "Hotel facility label for air conditioning in public areas",
  },
  "24-hour-reception": {
    defaultMessage: "24-Hours Front Desk",
    description: "Hotel facility label for 24-hour reception",
  },
  "towels-and-bed-linen": {
    defaultMessage: "Towels and bed linen",
    description: "Hotel facility label for towels and bed linen",
  },
  "lift-access": {
    defaultMessage: "Lift access",
    description: "Hotel facility label for lift access",
  },
  "car-park": {
    defaultMessage: "Car park",
    description: "Hotel facility label for car park",
  },
  "secure-parking": {
    defaultMessage: "Secure parking",
    description: "Hotel facility label for secure parking",
  },
  "valet-parking": {
    defaultMessage: "Valet parking",
    description: "Hotel facility label for valet parking",
  },
  "wheelchair-accessible": {
    defaultMessage: "Wheelchair-accessible",
    description: "Hotel facility label for wheelchair accessible",
  },
  "room-service": {
    defaultMessage: "Room service",
    description: "Hotel facility label for room service",
  },
  "laundry-service": {
    defaultMessage: "Laundry service",
    description: "Hotel facility label for laundry service",
  },
  "luggage-room": {
    defaultMessage: "Luggage room",
    description: "Hotel facility label for luggage room",
  },
  "airport-shuttle": {
    defaultMessage: "Airport shuttle",
    description: "Hotel facility label for airport shuttle",
  },
  "car-hire": {
    defaultMessage: "Car hire",
    description: "Hotel facility label for car hire",
  },
  gym: { defaultMessage: "Gym", description: "Hotel facility label for gym" },
  restaurant: {
    defaultMessage: "Restaurant",
    description: "Hotel facility label for restaurant",
  },
  "swimming-pool": {
    defaultMessage: "Swimming Pool",
    description: "Hotel facility label for swimming pool",
  },
  breakfast: {
    defaultMessage: "Breakfast",
    description: "Hotel facility label for breakfast",
  },
  "multilingual-staff": {
    defaultMessage: "Multilingual staff",
    description: "Hotel facility label for multilingual staff",
  },
  "24-hour-security": {
    defaultMessage: "24-hour security",
    description: "Hotel facility label for 24-hour security",
  },
  "spa-centre": {
    defaultMessage: "Spa centre",
    description: "Hotel facility label for spa centre",
  },
  sauna: {
    defaultMessage: "Sauna",
    description: "Hotel facility label for sauna",
  },
  bar: { defaultMessage: "Bar", description: "Hotel facility label for bar" },
  concierge: {
    defaultMessage: "Concierge",
    description: "Hotel facility label for concierge",
  },
  "babysitting-service": {
    defaultMessage: "Babysitting service",
    description: "Hotel facility label for babysitting service",
  },
  "children-playground": {
    defaultMessage: "Children playground",
    description: "Hotel facility label for children playground",
  },
  "business-centre": {
    defaultMessage: "Business centre",
    description: "Hotel facility label for business centre",
  },
  "meeting-room": {
    defaultMessage: "Meeting room",
    description: "Hotel facility label for meeting room",
  },
  casino: {
    defaultMessage: "Casino",
    description: "Hotel facility label for casino",
  },
  nightclub: {
    defaultMessage: "Nightclub",
    description: "Hotel facility label for nightclub",
  },
  "electric-kettle": {
    defaultMessage: "Electric kettle",
    description: "Hotel facility label for electric kettle",
  },
  garage: {
    defaultMessage: "Garage",
    description: "Hotel facility label for garage",
  },
  tennis: {
    defaultMessage: "Tennis",
    description: "Hotel facility label for tennis",
  },
  "small-pets-allowed": {
    defaultMessage: "Small pets allowed",
    description: "Hotel facility label for small pets allowed",
  },
  "check-in-check-out-key-collection": {
    defaultMessage: "Check-in / Check-out / Key Collection",
    description: "Hotel facility label for check-in check-out key collection",
  },
  "central-heating": {
    defaultMessage: "Central heating",
    description: "Hotel facility label for central heating",
  },
  "internet-access": {
    defaultMessage: "Internet access",
    description: "Hotel facility label for internet access",
  },
  "disability-friendly-bathroom": {
    defaultMessage: "Disability-friendly bathroom",
    description: "Hotel facility label for disability-friendly bathroom",
  },
  balcony: {
    defaultMessage: "Balcony",
    description: "Hotel facility label for balcony",
  },
  "extra-beds-on-demand": {
    defaultMessage: "Extra beds on demand",
    description: "Hotel facility label for extra beds on demand",
  },
  "towel-menu": {
    defaultMessage: "Towel menu",
    description: "Hotel facility label for towel menu",
  },
  "children-share-bed": {
    defaultMessage: "Children share the bed with parents",
    description:
      "Hotel facility label for children sharing the bed with parents",
  },
  "cot-on-demand": {
    defaultMessage: "Cot on demand",
    description: "Hotel facility label for cot on demand",
  },
  "smoking-rooms": {
    defaultMessage: "Smoking rooms",
    description: "Hotel facility label for smoking rooms",
  },
  hairdryer: {
    defaultMessage: "Hairdryer",
    description: "Hotel facility label for hairdryer",
  },
  "cable-tv": {
    defaultMessage: "Cable TV",
    description: "Hotel facility label for cable TV",
  },
  "carpeted-floors": {
    defaultMessage: "Carpeted floors",
    description: "Hotel facility label for carpeted floors",
  },
  dishwasher: {
    defaultMessage: "Dishwasher",
    description: "Hotel facility label for dishwasher",
  },
  fridge: {
    defaultMessage: "Fridge",
    description: "Hotel facility label for fridge",
  },
  "ironing-set": {
    defaultMessage: "Ironing set",
    description: "Hotel facility label for ironing set",
  },
  kitchen: {
    defaultMessage: "Kitchen",
    description: "Hotel facility label for kitchen",
  },
  kitchenette: {
    defaultMessage: "Kitchenette",
    description: "Hotel facility label for kitchenette",
  },
  "kitchen-utensils": {
    defaultMessage: "Kitchen utensils",
    description: "Hotel facility label for kitchen utensils",
  },
  "make-up-mirror": {
    defaultMessage: "Make-up mirror",
    description: "Hotel facility label for make-up mirror",
  },
  microwave: {
    defaultMessage: "Microwave",
    description: "Hotel facility label for microwave",
  },
  "plates-and-cups": {
    defaultMessage: "Plates & cups",
    description: "Hotel facility label for plates and cups",
  },
  "tea-and-coffee-making-facilities": {
    defaultMessage: "Tea and coffee making facilities",
    description: "Hotel facility label for tea and coffee making facilities",
  },
  toiletries: {
    defaultMessage: "Toiletries",
    description: "Hotel facility label for toiletries",
  },
  "washing-machine": {
    defaultMessage: "Washing machine",
    description: "Hotel facility label for washing machine",
  },
  safe: {
    defaultMessage: "Safe",
    description: "Hotel facility label for safe",
  },
  "pillow-menu": {
    defaultMessage: "Pillow menu",
    description: "Hotel facility label for pillow menu",
  },
  "wake-up-service": {
    defaultMessage: "Wake-up service",
    description: "Hotel facility label for wake-up service",
  },
  "pay-movies": {
    defaultMessage: "Pay movies",
    description: "Hotel facility label for pay movies",
  },
  "220v-power-supply": {
    defaultMessage: "220V power supply",
    description: "Hotel facility label for 220V power supply",
  },
  "alarm-clock": {
    defaultMessage: "Alarm clock",
    description: "Hotel facility label for alarm clock",
  },
  "background-music": {
    defaultMessage: "Background music",
    description: "Hotel facility label for background music",
  },
  bathrobes: {
    defaultMessage: "Bathrobes",
    description: "Hotel facility label for bathrobes",
  },
  bathroom: {
    defaultMessage: "Bathroom",
    description: "Hotel facility label for bathroom",
  },
  "shared-bathroom": {
    defaultMessage: "Shared bathroom",
    description: "Hotel facility label for shared bathroom",
  },
  bathtub: {
    defaultMessage: "Bathtub",
    description: "Hotel facility label for bathtub",
  },
  desk: {
    defaultMessage: "Desk",
    description: "Hotel facility label for desk",
  },
  "direct-dial-telephone": {
    defaultMessage: "Direct dial telephone",
    description: "Hotel facility label for direct dial telephone",
  },
  "individually-adjustable-air-conditioning": {
    defaultMessage: "Individually adjustable air conditioning",
    description:
      "Hotel facility label for individually adjustable air conditioning",
  },
  "individually-adjustable-heating": {
    defaultMessage: "Individually adjustable heating",
    description: "Hotel facility label for individually adjustable heating",
  },
  "mini-fridge": {
    defaultMessage: "Mini fridge",
    description: "Hotel facility label for mini fridge",
  },
  minibar: {
    defaultMessage: "Minibar",
    description: "Hotel facility label for minibar",
  },
  "plug-adaptor": {
    defaultMessage: "Plug adaptor",
    description: "Hotel facility label for plug adaptor",
  },
  radio: {
    defaultMessage: "Radio",
    description: "Hotel facility label for radio",
  },
  shower: {
    defaultMessage: "Shower",
    description: "Hotel facility label for shower",
  },
  slippers: {
    defaultMessage: "Slippers",
    description: "Hotel facility label for slippers",
  },
  tv: {
    defaultMessage: "TV",
    description: "Hotel facility label for TV",
  },
  "bicycle-hire-service": {
    defaultMessage: "Bicycle hire service",
    description: "Hotel facility label for bicycle hire service",
  },
  garden: {
    defaultMessage: "Garden",
    description: "Hotel facility label for garden",
  },
  "hotel-safe": {
    defaultMessage: "Hotel safe",
    description: "Hotel facility label for hotel safe",
  },
  shop: {
    defaultMessage: "Shop",
    description: "Hotel facility label for shop",
  },
  "late-check-out": {
    defaultMessage: "Late check-out",
    description: "Hotel facility label for late check-out",
  },
  "check-in-hour": {
    defaultMessage: "Check-in hour",
    description: "Hotel facility label for check-in hour",
  },
  "check-out-hour": {
    defaultMessage: "Check-out hour",
    description: "Hotel facility label for check-out hour",
  },
  "hot-tub": {
    defaultMessage: "Hot tub",
    description: "Hotel facility label for hot tub",
  },
  housekeeping: {
    defaultMessage: "Housekeeping",
    description: "Hotel facility label for housekeeping",
  },
  "centrally-regulated-air-conditioning": {
    defaultMessage: "Centrally regulated air conditioning",
    description:
      "Hotel facility label for centrally regulated air conditioning",
  },
  fan: {
    defaultMessage: "Fan",
    description: "Hotel facility label for fan",
  },
  "satellite-tv": {
    defaultMessage: "Satellite TV",
    description: "Hotel facility label for satellite TV",
  },
  "telephone-in-the-bathroom": {
    defaultMessage: "Telephone in the bathroom",
    description: "Hotel facility label for telephone in the bathroom",
  },
  "towels-and-bed-linen-at-surcharge": {
    defaultMessage: "Towels and bed linen at surcharge",
    description: "Hotel facility label for towels and bed linen at surcharge",
  },
  "trouser-press": {
    defaultMessage: "Trouser press",
    description: "Hotel facility label for trouser press",
  },
  "smoke-detector": {
    defaultMessage: "Smoke detector",
    description: "Hotel facility label for smoke detector",
  },
  "bellboy-service": {
    defaultMessage: "Bellboy service",
    description: "Hotel facility label for bellboy service",
  },
  cloakroom: {
    defaultMessage: "Cloakroom",
    description: "Hotel facility label for cloakroom",
  },
  "currency-exchange-facilities": {
    defaultMessage: "Currency exchange",
    description: "Hotel facility label for currency exchange facilities",
  },
  "newspaper-stand": {
    defaultMessage: "Newspaper stand",
    description: "Hotel facility label for newspaper stand",
  },
  supermarket: {
    defaultMessage: "Supermarket",
    description: "Hotel facility label for supermarket",
  },
  "transfer-service": {
    defaultMessage: "Transfer service",
    description: "Hotel facility label for transfer service",
  },
  "bicycle-storage": {
    defaultMessage: "Bicycle storage",
    description: "Hotel facility label for bicycle storage",
  },
  "dining-area": {
    defaultMessage: "Dining area",
    description: "Hotel facility label for dining area",
  },
  "clothes-dryer": {
    defaultMessage: "Clothes dryer",
    description: "Hotel facility label for clothes dryer",
  },
  "mobile-phone-coverage": {
    defaultMessage: "Mobile phone coverage",
    description: "Hotel facility label for mobile phone coverage",
  },
  "wired-internet": {
    defaultMessage: "Wired internet",
    description: "Hotel facility label for wired internet",
  },
  "turndown-service": {
    defaultMessage: "Turndown service",
    description: "Hotel facility label for turndown service",
  },
  newspapers: {
    defaultMessage: "Newspapers",
    description: "Hotel facility label for newspapers",
  },
  library: {
    defaultMessage: "Library",
    description: "Hotel facility label for library",
  },
  "sun-terrace": {
    defaultMessage: "Sun terrace",
    description: "Hotel facility label for sun terrace",
  },
  terrace: {
    defaultMessage: "Terrace",
    description: "Hotel facility label for terrace",
  },
  toaster: {
    defaultMessage: "Toaster",
    description: "Hotel facility label for toaster",
  },
  fireplace: {
    defaultMessage: "Fireplace",
    description: "Hotel facility label for fireplace",
  },
  waterpark: {
    defaultMessage: "Waterpark",
    description: "Hotel facility label for waterpark",
  },
  "childrens-swimming-area": {
    defaultMessage: "Children's swimming area",
    description: "Hotel facility label for children's swimming area",
  },
  "medical-service": {
    defaultMessage: "Medical service",
    description: "Hotel facility label for medical service",
  },
  "partners-bike-shop-workshop": {
    defaultMessage: "Partner's bike shop/workshop",
    description: "Hotel facility label for partner's bike shop/workshop",
  },
  "key-collection": {
    defaultMessage: "Key Collection",
    description: "Hotel facility label for key collection",
  },
  "local-and-international-calls": {
    defaultMessage: "Local and international calls",
    description: "Hotel facility label for local and international calls",
  },
  "non-smoking-area": {
    defaultMessage: "Non-smoking area",
    description: "Hotel facility label for non-smoking area",
  },
  highchairs: {
    defaultMessage: "Highchairs",
    description: "Hotel facility label for highchairs",
  },
  "banquet-hall": {
    defaultMessage: "Banquet hall",
    description: "Hotel facility label for banquet hall",
  },
  "air-conditioning-in-restaurant": {
    defaultMessage: "Air conditioning in restaurant",
    description: "Hotel facility label for air conditioning in restaurant",
  },
  "24h-dining-cafe": {
    defaultMessage: "24h dining café",
    description: "Hotel facility label for 24h dining café",
  },
  "rooftop-bar": {
    defaultMessage: "Rooftop bar",
    description: "Hotel facility label for rooftop bar",
  },
  cafe: {
    defaultMessage: "Café",
    description: "Hotel facility label for café",
  },
  "conference-room": {
    defaultMessage: "Conference room",
    description: "Hotel facility label for conference room",
  },
  projector: {
    defaultMessage: "Projector",
    description: "Hotel facility label for projector",
  },
  "conference-hostess": {
    defaultMessage: "Conference hostess",
    description: "Hotel facility label for conference hostess",
  },
  photocopier: {
    defaultMessage: "Photocopier",
    description: "Hotel facility label for photocopier",
  },
  printer: {
    defaultMessage: "Printer",
    description: "Hotel facility label for printer",
  },
  "audio-visual-equipment-rental": {
    defaultMessage: "Audio-visual equipment rental",
    description: "Hotel facility label for audio-visual equipment rental",
  },
  "theatre-auditorium": {
    defaultMessage: "Theatre / auditorium",
    description: "Hotel facility label for theatre / auditorium",
  },
  "game-room": {
    defaultMessage: "Game room",
    description: "Hotel facility label for game room",
  },
  "steam-bath": {
    defaultMessage: "Steam bath",
    description: "Hotel facility label for steam bath",
  },
  massage: {
    defaultMessage: "Massage",
    description: "Hotel facility label for massage",
  },
  "spa-treatments": {
    defaultMessage: "Spa treatments",
    description: "Hotel facility label for spa treatments",
  },
  "turkish-bath-hamam": {
    defaultMessage: "Turkish bath (hamam)",
    description: "Hotel facility label for Turkish bath (hamam)",
  },
  "banana-boating": {
    defaultMessage: "Banana boating",
    description: "Hotel facility label for banana boating",
  },
  waterskiing: {
    defaultMessage: "Waterskiing",
    description: "Hotel facility label for waterskiing",
  },
  "motor-board-ride": {
    defaultMessage: "Motor board ride",
    description: "Hotel facility label for motor board ride",
  },
  windsurfing: {
    defaultMessage: "Windsurfing",
    description: "Hotel facility label for windsurfing",
  },
  sailing: {
    defaultMessage: "Sailing",
    description: "Hotel facility label for sailing",
  },
  "catamaran-sailing": {
    defaultMessage: "Catamaran sailing",
    description: "Hotel facility label for catamaran sailing",
  },
  canoeing: {
    defaultMessage: "Canoeing",
    description: "Hotel facility label for canoeing",
  },
  aerobics: {
    defaultMessage: "Aerobics",
    description: "Hotel facility label for aerobics",
  },
  archery: {
    defaultMessage: "Archery",
    description: "Hotel facility label for archery",
  },
  basketball: {
    defaultMessage: "Basketball",
    description: "Hotel facility label for basketball",
  },
  volleyball: {
    defaultMessage: "Volleyball",
    description: "Hotel facility label for volleyball",
  },
  billiards: {
    defaultMessage: "Billiards",
    description: "Hotel facility label for billiards",
  },
  "mini-golf": {
    defaultMessage: "Mini golf",
    description: "Hotel facility label for mini golf",
  },
  surfing: {
    defaultMessage: "Surfing",
    description: "Hotel facility label for surfing",
  },
  "paddle-tennis": {
    defaultMessage: "Paddle tennis",
    description: "Hotel facility label for paddle tennis",
  },
  badminton: {
    defaultMessage: "Badminton",
    description: "Hotel facility label for badminton",
  },
  darts: {
    defaultMessage: "Darts",
    description: "Hotel facility label for darts",
  },
  "putting-green": {
    defaultMessage: "Putting green",
    description: "Hotel facility label for putting green",
  },
  skiing: {
    defaultMessage: "Skiing",
    description: "Hotel facility label for skiing",
  },
  tobogganing: {
    defaultMessage: "Tobogganing",
    description: "Hotel facility label for tobogganing",
  },
  "golf-practice-facility": {
    defaultMessage: "Golf practice facility",
    description: "Hotel facility label for golf practice facility",
  },
  "aqua-fit": {
    defaultMessage: "Aqua fit",
    description: "Hotel facility label for aqua fit",
  },
  "smoking-area": {
    defaultMessage: "Smoking area",
    description: "Hotel facility label for smoking area",
  },
  "cycling-mountain-biking": {
    defaultMessage: "Cycling / mountain biking",
    description: "Hotel facility label for cycling / mountain biking",
  },
  diving: {
    defaultMessage: "Diving",
    description: "Hotel facility label for diving",
  },
  fitness: {
    defaultMessage: "Fitness",
    description: "Hotel facility label for fitness",
  },
  golf: {
    defaultMessage: "Golf",
    description: "Hotel facility label for golf",
  },
  "pedal-boating": {
    defaultMessage: "Pedal boating",
    description: "Hotel facility label for pedal boating",
  },
  squash: {
    defaultMessage: "Squash",
    description: "Hotel facility label for squash",
  },
  "table-tennis": {
    defaultMessage: "Table tennis",
    description: "Hotel facility label for table tennis",
  },
  pub: {
    defaultMessage: "Pub",
    description: "Hotel facility label for pub",
  },
  "living-room": {
    defaultMessage: "Living room: {count}",
    description: "Hotel facility label for living room with numeric value",
  },
  "number-of-bedrooms": {
    defaultMessage: "Number of bedrooms: {count}",
    description:
      "Hotel facility label for number of bedrooms with numeric value",
  },
  "room-size-sqm": {
    defaultMessage: "Room size: {count} sqm",
    description:
      "Hotel facility label for room size in square metres with numeric value",
  },
});

const formatLocalizedLabel = (
  intl: IntlShape,
  descriptor: MessageDescriptor | undefined,
  fallbackLabel: string,
  values?: Record<string, PrimitiveType>,
): string => {
  if (!descriptor) {
    return fallbackLabel;
  }

  return intl.formatMessage(descriptor, values);
};

export const getLocalizedCategoryLabel = (
  intl: IntlShape,
  categoryKey: FacilityCategoryKey,
  fallbackLabel: string,
): string =>
  formatLocalizedLabel(intl, CATEGORY_MESSAGES[categoryKey], fallbackLabel);

export const getLocalizedFacilityLabel = (
  intl: IntlShape,
  facilityKey: string | undefined,
  fallbackLabel: string,
  values?: Record<string, PrimitiveType>,
): string => {
  if (!facilityKey) {
    return fallbackLabel;
  }

  return formatLocalizedLabel(
    intl,
    FACILITY_MESSAGES[facilityKey as keyof typeof FACILITY_MESSAGES],
    fallbackLabel,
    values,
  );
};
